<template>
  <div class="user-manage">
    <el-card>
      <!-- 表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column label="用户名" prop="userName" align="center" />
        <el-table-column label="头像" align="center">
          <template #default="{ row }">
            <el-avatar :size="40" :src="row.photoUrl">
              <el-icon><UserFilled /></el-icon>
            </el-avatar>
          </template>
        </el-table-column>
        <el-table-column label="邮箱" prop="email" align="center" />
        <el-table-column label="手机号" prop="phoneNum" align="center" />
        <el-table-column label="OpenID" prop="openid" align="center" />
        <el-table-column label="用户类型" align="center">
          <template #default="{ row }">
            <el-select
              align="center"
              v-model="row.userType"
              @change="handleUserTypeChange(row)"
              placeholder="选择用户类型"
            >
              <el-option label="普通用户" :value="0" />
              <el-option label="VIP" :value="1" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="200">
          <template #default="scope">
            <el-button
              link
              type="primary"
              :icon="View"
              @click="handleView(scope.row)"
            >
              查看
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="queryParams.page"
        v-model:page-size="queryParams.pageSize"
        :total="total"
        :page-sizes="[10, 20, 30, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />

      <!-- 查看抽屉 -->
      <el-drawer
        v-model="drawer"
        title="查看用户详情"
        direction="rtl"
        size="500px"
      >
        <el-descriptions :column="1" border>
          <el-descriptions-item label="用户名">
            {{ viewData.userName }}
          </el-descriptions-item>
          <el-descriptions-item label="用户类型">
            {{ String(viewData.userType) === "1" ? "VIP" : "普通用户" }}
          </el-descriptions-item>
          <el-descriptions-item label="OpenID">
            {{ viewData.openid }}
          </el-descriptions-item>
          <el-descriptions-item label="头像">
            <el-avatar
              v-if="viewData.photoUrl"
              :size="100"
              :src="viewData.photoUrl"
            >
              <el-icon><UserFilled /></el-icon>
            </el-avatar>
            <el-icon v-else :size="100"><UserFilled /></el-icon>
          </el-descriptions-item>
          <el-descriptions-item label="邮箱">
            {{ viewData.email }}
          </el-descriptions-item>
          <el-descriptions-item label="手机号">
            {{ viewData.phoneNum }}
          </el-descriptions-item>
        </el-descriptions>
      </el-drawer>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import { ElMessage } from "element-plus";
import { Edit, UserFilled, View } from "@element-plus/icons-vue";
import { getUserList, updateUserType } from "@/api/user";
import type { UserItem, QueryParams } from "@/user/type";

// 表格数据
const loading = ref(false);
const tableData = ref<UserItem[]>([]);
const total = ref(0);
const ids = ref<string[]>([]);

// 查询参数
const queryParams = reactive<QueryParams>({
  page: 1,
  pageSize: 10,
});
// 抽屉控制
const drawer = ref(false);

// 查看数据
const viewData = ref({
  userName: "",
  userType: "",
  openid: "",
  photoUrl: "",
  email: "",
  phoneNum: "",
});

// 查看用户
const handleView = (row: UserItem) => {
  viewData.value = {
    userName: row.userName || "",
    photoUrl: row.photoUrl || "",
    email: row.email || "",
    phoneNum: row.phoneNum || "",
    usertype: String(row.usertype) || "0",
    openid: row.openid || "",
  };
  drawer.value = true;
};
// 获取数据列表
function getList() {
  loading.value = true;
  try {
    console.log("userManage.vue中getList方法queryParams:", queryParams);
    // 如果有 standardId，则从服务器获取数据
    getUserList({
      page: queryParams.page,
      pageSize: queryParams.pageSize,
    }).then((response) => {
      if (response && response.data) {
        tableData.value = response.data.records || [];
        total.value = response.data.total || 0;
      }
    });
  } catch (error) {
    console.error("获取数据失败:", error);
    ElMessage.error("获取数据失败");
  } finally {
    loading.value = false;
  }
}
// 分页处理
const handleSizeChange = (val: number) => {
  queryParams.pageSize = val;
  getList();
};

const handleCurrentChange = (val: number) => {
  queryParams.page = val;
  getList();
};

// 表格选择
const handleSelectionChange = (selection: UserItem[]) => {
  ids.value = selection.map((item) => item.userName);
};

// 用户类型变更处理
const handleUserTypeChange = async (row: UserItem) => {
  try {
    const response = await updateUserType(row.id, row.userType);
    if (response.code === 1) {
      ElMessage.success("用户类型更新成功");
    } else {
      ElMessage.error(response.msg || "更新用户类型失败");
    }
  } catch (error) {
    console.error("更新用户类型失败:", error);
    ElMessage.error("更新用户类型失败，请重试");
  }
};

getList();
</script>

<style scoped>
.user-manage {
  padding: 20px;
}

.mb8 {
  margin-bottom: 8px;
}

.avatar-uploader {
  text-align: center;
}

.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}
:deep(.el-select-dropdown__item) {
  text-align: center;
}
</style>
```
